import React, {createContext, useContext, useState, useCallback, FC, ReactNode, useEffect} from 'react';
import { useHistory } from 'react-router-dom';
import { isEqual, isFunction } from 'lodash';
import {getNotificationsSubjects} from "../actions/notificationsActions";
import {Option} from "../../_common/types";

type QueryParams = Record<string, string>;

type QueryParamsUpdater = QueryParams | ((prev: QueryParams) => QueryParams);

interface NotificationsContextValue {
  queryParams: QueryParams;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  setQueryParams: (nextQueryParams: QueryParamsUpdater) => void;
  notificationsSubjects: Option[]
}

interface NotificationsProviderProps {
  children: ReactNode;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotificationsContext(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotificationsContext must be used within a NotificationsProvider');
  }
  return context;
}

export const NotificationsProvider: FC<NotificationsProviderProps> = ({ children }) => {
  const history = useHistory();
  const [queryParams, setQueryParamsBase] = useState<QueryParams>({});
  const [notificationsSubjects, setNotificationsSubjects] = useState<Option[]>([]);

  const setQueryParams = useCallback((nextQueryParams: QueryParamsUpdater) => {
    setQueryParamsBase((prevQueryParams) => {
      if (isFunction(nextQueryParams)) {
        nextQueryParams = nextQueryParams(prevQueryParams);
      }

      if (isEqual(prevQueryParams, nextQueryParams)) {
        return prevQueryParams;
      }

      const cleaned = Object.fromEntries(
        Object.entries(nextQueryParams).filter(([, v]) => Boolean(v))
      ) as QueryParams;

      const path = history.location.pathname;
      const newQueryParams = new URLSearchParams(cleaned);
      history.replace(`${path}?${newQueryParams.toString()}`);

      return cleaned;
    });
  }, [history]);

  useEffect(() => {
    getNotificationsSubjects().then(setNotificationsSubjects)
  }, [])

  const value: NotificationsContextValue = {
    queryParams,
    setQueryParamsBase,
    setQueryParams,
    notificationsSubjects
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};